// Client-side image compression for photo uploads.
//
// Phone-camera photos are frequently 10–20MB (or HEIC that decodes to ~48MB of
// pixels); uploading them at full size is the single biggest reason bulk photo
// upload is slow. We downscale to a max dimension and re-encode as JPEG in the
// browser before sending, which cuts a typical photo to a few hundred KB with
// no meaningful quality loss for the documentation use these serve.
//
// Memory note (iOS Safari): decoding a full-resolution photo into an
// ImageBitmap holds width×height×4 bytes (~48MB for a 12MP shot). Doing that
// for several photos at once blows iOS Safari's per-tab memory ceiling, which
// is why bulk upload failed on iPhone but not on desktop. We therefore decode
// through an <img> element — Safari can decode large <img>s at reduced
// resolution and releases them — draw straight to the small target canvas, and
// explicitly release the canvas backing store afterward.
//
// This is best-effort: any decode/encode failure (e.g. a HEIC file a desktop
// browser can't decode) returns the ORIGINAL file untouched so a photo is
// never silently dropped. Callers upload whatever comes back.

const DEFAULT_MAX_DIM = 2000; // longest edge, px
const DEFAULT_QUALITY = 0.82; // JPEG quality

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function compressImage(file, opts = {}) {
  const { maxDim = DEFAULT_MAX_DIM, quality = DEFAULT_QUALITY } = opts;

  if (!file || !file.type || !file.type.startsWith("image/")) return file;

  const url = URL.createObjectURL(file);
  let canvas;
  try {
    const img = await loadImage(url);
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return file;

    const scale = Math.min(1, maxDim / Math.max(w, h));
    const targetW = Math.max(1, Math.round(w * scale));
    const targetH = Math.max(1, Math.round(h * scale));

    canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // Modern browsers apply EXIF orientation when rendering the <img>, so the
    // drawn pixels come out upright.
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await canvasToBlob(canvas, "image/jpeg", quality);
    // Keep whichever is smaller — never make a file bigger (e.g. an already
    // small, already-compressed image).
    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name ? file.name.replace(/\.[^.]+$/, "") : "photo";
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
    // Free the canvas backing store immediately — iOS Safari otherwise keeps
    // it around and accumulates memory across a large batch.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
